import { z } from "zod";

export const jobStatusSchema = z.enum(["pending", "processing", "completed", "failed"]);
export type JobStatus = z.infer<typeof jobStatusSchema>;

export const jobTypeSchema = z.enum([
  "sync_variables",
  "sync_master_items",
  "sync_app",
  "audit_group",
  "sync_group",
  "audit_all_groups",
  "sync_all_groups",
  "analyze_app",
  "analyze_qvds",
  "collect_capacity",
  "collect_reloads",
  "poll_app_evaluation",
]);
export type JobType = z.infer<typeof jobTypeSchema>;

// Jobs nobody asks for, left out of the jobsPerMonth gate and count:
// poll_app_evaluation continues an analysis that already charged its quota;
// collect_reloads is enqueued every few minutes by the reload-analysis
// scheduler and would drain any finite plan on its own.
export const QUOTA_EXEMPT_JOB_TYPES: string[] = ["poll_app_evaluation", "collect_reloads"];

// Variable change item for batch processing
export const variableChangeSchema = z.object({
  connectionId: z.string().uuid(),
  appId: z.string(),
  appName: z.string().optional(),
  // For creates, this is a client-generated id (temp id)
  variableId: z.string(),
  variableName: z.string(),
  action: z.enum(["update", "create", "delete"]).default("update"),
  changes: z.object({
    name: z.string().optional(),
    definition: z.string().optional(),
    comment: z.string().optional(),
  }),
});

export type VariableChange = z.infer<typeof variableChangeSchema>;

// Job payload for sync_variables type
export const syncVariablesPayloadSchema = z.object({
  changes: z.array(variableChangeSchema),
});

export type SyncVariablesPayload = z.infer<typeof syncVariablesPayloadSchema>;

// Master item change item for batch processing
export const masterItemChangeSchema = z.object({
  connectionId: z.string().uuid(),
  appId: z.string(),
  appName: z.string().optional(),
  itemId: z.string(),
  itemType: z.enum(["dimension", "measure"]),
  itemTitle: z.string(),
  action: z.enum(["update", "create", "delete"]).default("update"),
  changes: z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    // Dimension-specific
    fieldDefs: z.array(z.string()).optional(),
    fieldLabels: z.array(z.string()).optional(),
    grouping: z.enum(["N", "H", "C"]).optional(),
    labelExpression: z.string().optional(),
    // Measure-specific
    expression: z.string().optional(),
    label: z.string().optional(),
    tags: z.array(z.string()).optional(),
    numberFormat: z.string().optional(),
    color: z.string().optional(),
  }),
});

export type MasterItemChange = z.infer<typeof masterItemChangeSchema>;

// Job payload for sync_master_items type
export const syncMasterItemsPayloadSchema = z.object({
  changes: z.array(masterItemChangeSchema),
});

export type SyncMasterItemsPayload = z.infer<typeof syncMasterItemsPayloadSchema>;

// Job payload for sync_app type (combines variables and master items)
export const syncAppPayloadSchema = z.object({
  variableChanges: z.array(variableChangeSchema).optional(),
  masterItemChanges: z.array(masterItemChangeSchema).optional(),
});

export type SyncAppPayload = z.infer<typeof syncAppPayloadSchema>;

// Job payload for audit_group type
export const auditGroupPayloadSchema = z.object({
  groupId: z.string().uuid(),
  appIds: z.array(z.string()).optional(), // Empty = all apps in group
  variableIds: z.array(z.string().uuid()).optional(), // Empty = all variables in group
  dimensionIds: z.array(z.string().uuid()).optional(), // Empty = all dimensions in group
  measureIds: z.array(z.string().uuid()).optional(), // Empty = all measures in group
});

export type AuditGroupPayload = z.infer<typeof auditGroupPayloadSchema>;

// Conflict resolution action for sync
export const conflictResolutionSchema = z.enum(["push", "pull", "skip"]);
export type ConflictResolution = z.infer<typeof conflictResolutionSchema>;

// Job payload for sync_group type
export const syncGroupPayloadSchema = z.object({
  groupId: z.string().uuid(),
  appIds: z.array(z.string()).optional(), // Empty = all apps in group
  variableIds: z.array(z.string().uuid()).optional(), // Empty = all variables in group
  dimensionIds: z.array(z.string().uuid()).optional(), // Empty = all dimensions in group
  measureIds: z.array(z.string().uuid()).optional(), // Empty = all measures in group
  // Conflict resolutions: itemId -> resolution action
  // If not provided for an out_of_sync item, defaults to 'push'
  conflictResolutions: z.record(z.string(), conflictResolutionSchema).optional(),
});

export type SyncGroupPayload = z.infer<typeof syncGroupPayloadSchema>;

// Job payload for audit_all_groups type
export const auditAllGroupsPayloadSchema = z.object({
  organizationId: z.string(),
});

export type AuditAllGroupsPayload = z.infer<typeof auditAllGroupsPayloadSchema>;

// Job payload for sync_all_groups type
export const syncAllGroupsPayloadSchema = z.object({
  organizationId: z.string(),
  // Conflict resolutions applied uniformly to all groups: itemId -> resolution action
  conflictResolutions: z.record(z.string(), conflictResolutionSchema).optional(),
});

export type SyncAllGroupsPayload = z.infer<typeof syncAllGroupsPayloadSchema>;

// Job payload for poll_app_evaluation type.
//
// A job of its own, not a second phase of analyze_app: Qlik's evaluation ran
// 11 minutes on a real app while the static analysis finishes in seconds, so it
// needs its own deadline and its own cancellation.
export const pollAppEvaluationPayloadSchema = z.object({
  connectionId: z.string().uuid(),
  appId: z.string(),
  // The analysis that asked for the evaluation. Absent for a legacy analyze_app
  // job (no lifecycle row).
  analysisId: z.string().uuid().optional(),
  // The app_qlik_evaluations row the poller writes back to.
  evaluationRowId: z.string().uuid(),
  // Absolute deadline from the Qlik POST, so dispatch/retries do not reset it.
  deadlineAt: z.string().datetime().optional(),
});

export type PollAppEvaluationPayload = z.infer<typeof pollAppEvaluationPayloadSchema>;

// Progress info
export const jobProgressSchema = z.object({
  total: z.number(),
  completed: z.number(),
  failed: z.number(),
  currentItem: z.string().optional(),
});

export type JobProgress = z.infer<typeof jobProgressSchema>;

// Result item
export const jobResultItemSchema = z.object({
  variableId: z.string().optional(),
  variableName: z.string().optional(),
  itemId: z.string().optional(),
  itemTitle: z.string().optional(),
  itemType: z.enum(["variable", "dimension", "measure", "analysis"]).optional(),
  appId: z.string(),
  appName: z.string().optional(),
  appUrl: z.string().optional(),
  success: z.boolean(),
  error: z.string().optional(),
  warnings: z.array(z.string()).optional(),
});

export type JobResultItem = z.infer<typeof jobResultItemSchema>;

// Job result
export const jobResultSchema = z.object({
  items: z.array(jobResultItemSchema),
  summary: z.object({
    total: z.number(),
    succeeded: z.number(),
    failed: z.number(),
  }),
});

export type JobResult = z.infer<typeof jobResultSchema>;

// Main job schema
export const jobSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  type: jobTypeSchema,
  status: jobStatusSchema,
  progress: jobProgressSchema.nullable(),
  payload: z.unknown(), // Will be validated based on type
  result: jobResultSchema.nullable(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
});

export type Job = z.infer<typeof jobSchema>;

// Create job input
export const createJobInputSchema = z.object({
  type: jobTypeSchema,
  payload: z.unknown(), // Validated based on type
});

export type CreateJobInput = z.infer<typeof createJobInputSchema>;

// API response schemas
export const listJobsResponseSchema = z.object({
  data: z.array(jobSchema),
  // Rows matching the request's filters, ignoring limit/offset — drives the
  // page-based pagination footer. Always present.
  total: z.number(),
  // Only for keyset (cursor) pagination; absent on the last page.
  nextCursor: z.string().uuid().optional(),
});

export type ListJobsResponse = z.infer<typeof listJobsResponseSchema>;

export const jobResponseSchema = z.object({
  data: jobSchema,
});

export type JobResponse = z.infer<typeof jobResponseSchema>;

// type/status arrive as comma-separated lists (the jobs screen's multi-selects);
// an empty/blank value means "no filter" rather than a validation error.
const csv = z
  .string()
  .optional()
  .transform((value) => {
    const values =
      value
        ?.split(",")
        .map((v) => v.trim())
        .filter(Boolean) ?? [];
    return values.length > 0 ? values : undefined;
  });

export const jobListQuerySchema = z
  .object({
    // Page offset for the jobs list; pairs with the `total` in the response.
    offset: z.coerce.number().int().min(0).optional(),
    type: csv.pipe(z.array(jobTypeSchema).optional()),
    status: csv.pipe(z.array(jobStatusSchema).optional()),
    // Free-text search over the job payload. Blank/whitespace-only means "no
    // filter" — a user typing spaces must not fail the request.
    q: z
      .string()
      .max(200)
      .optional()
      .transform((value) => value?.trim() || undefined),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
  })
  .passthrough();
